// AIDEV-NOTE: In-context AI disclosure + "not a substitute for clinical care" notice.
// Required on client-facing AI surfaces (add-part chat, meditations): the disclosure
// is a *precondition* of Colorado HB 26-1195's wellness-tool carve-out
// (C.R.S. 12-245-224.5(9), effective 2026-08-12) and aligns with Utah HB 452's
// AI-disclosure duty. Non-blocking and dismissible per surface — do not convert this
// into a modal or a consent gate; the statutes require conspicuous disclosure, not
// blocking acknowledgement. See docs/research/ai-therapy-regulation/landscape-2026-07.md.

import { useEffect, useState } from "react";

import { Link } from "@remix-run/react";
import { Sparkles } from "lucide-react";

import { Button } from "~/components/ui/button";
import { trackEvent } from "~/lib/analytics";
import { cn } from "~/lib/utils";

const DISMISS_KEY_PREFIX = "ai-disclosure-dismissed:";

export type AiDisclosureSurface = "add-part-chat" | "meditations";

// AIDEV-NOTE: Copy must keep both statutory elements: (1) it's AI, not a person,
// (2) not therapy / not a substitute for clinical care. Edit with care.
const SURFACE_COPY: Record<AiDisclosureSurface, string> = {
    "add-part-chat":
        "This conversation is guided by AI, not a person. It's here to help you capture what you already notice about a part — it isn't therapy, and isn't a substitute for clinical care.",
    meditations:
        "These meditations are generated by AI, not recorded by a person. They're a wellness practice to use alongside your sessions — not therapy, and not a substitute for clinical care.",
};

function dismissKey(surface: AiDisclosureSurface) {
    return `${DISMISS_KEY_PREFIX}${surface}`;
}

function hasDismissed(surface: AiDisclosureSurface): boolean {
    if (typeof window === "undefined") return false;
    try {
        return localStorage.getItem(dismissKey(surface)) === "true";
    } catch {
        return false;
    }
}

interface AiDisclosureBannerProps {
    surface: AiDisclosureSurface;
    className?: string;
}

/**
 * Full-width, dismissible AI disclosure for client-facing LLM views.
 * Renders nothing until mounted, so the dismissed state never causes a
 * hydration mismatch or a flash of the banner for users who already closed it.
 */
export function AiDisclosureBanner({
    surface,
    className,
}: AiDisclosureBannerProps) {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!hasDismissed(surface)) setVisible(true);
    }, [surface]);

    if (!visible) return null;

    const handleDismiss = () => {
        setVisible(false);
        try {
            localStorage.setItem(dismissKey(surface), "true");
        } catch {
            // Non-fatal: banner simply reappears next visit.
        }
        trackEvent("clicked button", {
            text: `ai disclosure dismissed - ${surface}`,
        });
    };

    return (
        <div
            className={cn(
                // AIDEV-NOTE: Background must stay fully opaque — the banner overlays chat
                // content, and a translucent fill let the conversation text bleed through it
                // (QA, Jul 2026). flex-wrap keeps "Got it" from colliding with the copy on mobile.
                "flex flex-wrap items-start gap-x-3 gap-y-2 rounded-lg border border-brand-forest-200 bg-brand-forest-50 px-4 py-3",
                className,
            )}
            data-testid={`ai-disclosure-banner-${surface}`}
        >
            <Sparkles className="mt-0.5 size-4 shrink-0 text-brand-forest-500" />
            <p className="min-w-[14rem] flex-1 text-sm leading-relaxed text-brand-forest-700">
                {SURFACE_COPY[surface]}{" "}
                <Link
                    to="/about-ai"
                    className="font-medium text-brand-forest-800 underline underline-offset-2 hover:text-brand-forest-900"
                >
                    Learn more
                </Link>
            </p>
            <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="ml-auto shrink-0 text-brand-forest-600 hover:bg-brand-forest-100 hover:text-brand-forest-800"
            >
                Got it
            </Button>
        </div>
    );
}
